import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { createStore, get, set, del, keys } from 'idb-keyval'
import { WorkspaceDocumentSchema, WorkspaceMetaSchema } from '@/types/schemas/workspace'
import type { WorkspaceDocument, WorkspaceMeta } from '@/types/schemas/workspace'
import { buildIdbKey } from '@/storage/version'
import { setStorageUserScope, getStorageUserScope } from '@/storage/user-scope'

// ─── IndexedDB Store ───

const store = createStore('edge-workspace', 'cache')

// ─── User scoping ───
// All cache keys are prefixed with the current user's sub so that
// switching users (or simulating a user from admin) gets isolated data.
// The scope itself lives in storage/user-scope.ts (shared with the
// chart-assets snapshot); these aliases keep auth call sites unchanged.

export const setIdbUserScope = setStorageUserScope
export const getIdbUserScope = getStorageUserScope

// ─── Key helpers ───

function docKey(wid: string, layoutId: string) {
  return buildIdbKey(getStorageUserScope(), 'doc', wid, layoutId)
}

function metaKey(wid: string) {
  return buildIdbKey(getStorageUserScope(), 'meta', wid)
}

// ─── Transaction helper ───

/**
 * Wrap the idb-keyval store callback so we wait for the IDB transaction
 * to actually commit, not just for the put requests to be queued.
 */
function txWrite(fn: (idbStore: IDBObjectStore) => void): Promise<void> {
  return store('readwrite', (idbStore) => {
    fn(idbStore)
    const tx = idbStore.transaction
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onabort = () => reject(tx.error)
      tx.onerror = () => reject(tx.error)
    })
  })
}

// ─── Public API ───

export async function getWorkspaceDoc(
  wid: string,
  layoutId: string,
): Promise<WorkspaceDocument | null> {
  try {
    const raw = await get(docKey(wid, layoutId), store)
    if (!raw) return null
    const result = WorkspaceDocumentSchema.safeParse(raw)
    if (!result.success) {
      console.warn('[idb-cache] WorkspaceDocument failed validation; treating as missing', {
        wid,
        layoutId,
        issues: result.error.issues,
      })
      // Silent local data loss: the cached copy is discarded and the user
      // never sees a message. Count only; issue text can carry state.
      trackEvent(EVENTS.WORKSPACE_DOC_VALIDATION_FAILED, {
        workspace_id: wid,
        layout_id: layoutId,
        kind: 'doc',
        issue_count: result.error.issues.length,
      })
      return null
    }
    return result.data
  } catch {
    return null
  }
}

export async function setWorkspaceDoc(
  wid: string,
  layoutId: string,
  doc: WorkspaceDocument,
): Promise<void> {
  try {
    await set(docKey(wid, layoutId), doc, store)
  } catch {
    /* ignore — IDB write failure is non-critical */
  }
}

/**
 * Evict a single layout doc from the cache. The next call to
 * `getWorkspaceDoc` for the same key returns null, forcing the caller
 * to refetch from the server. Used by the expressions propagation
 * cascade so that snapshot caches refreshed server-side don't get
 * shadowed by a stale IDB hit on cold start.
 */
export async function deleteWorkspaceDoc(wid: string, layoutId: string): Promise<void> {
  try {
    await del(docKey(wid, layoutId), store)
  } catch {
    /* ignore */
  }
}

// ─── Last-known-good backup slot ───
//
// Written immediately BEFORE external content (server refetch, cross-tab
// hydrate) overwrites local layout state. One slot per (wid, layoutId),
// overwritten each time. Insurance against a stale/wrong payload clobbering a
// good layout — the previous local state stays recoverable from here.

function backupKey(wid: string, layoutId: string) {
  return buildIdbKey(getStorageUserScope(), 'docbak', wid, layoutId)
}

export async function backupWorkspaceDoc(
  wid: string,
  layoutId: string,
  doc: WorkspaceDocument,
): Promise<void> {
  try {
    await set(backupKey(wid, layoutId), doc, store)
  } catch {
    /* ignore — backup is best-effort */
  }
}

/** Read the last-known-good backup (manual recovery path). */
export async function getWorkspaceDocBackup(
  wid: string,
  layoutId: string,
): Promise<WorkspaceDocument | null> {
  try {
    const raw = await get(backupKey(wid, layoutId), store)
    if (!raw) return null
    const result = WorkspaceDocumentSchema.safeParse(raw)
    return result.success ? result.data : null
  } catch {
    return null
  }
}

export async function getWorkspaceMeta(wid: string): Promise<WorkspaceMeta | null> {
  try {
    const raw = await get(metaKey(wid), store)
    if (!raw) return null
    const result = WorkspaceMetaSchema.safeParse(raw)
    if (!result.success) {
      console.warn('[idb-cache] WorkspaceMeta failed validation; treating as missing', {
        wid,
        issues: result.error.issues,
      })
      trackEvent(EVENTS.WORKSPACE_DOC_VALIDATION_FAILED, {
        workspace_id: wid,
        kind: 'meta',
        issue_count: result.error.issues.length,
      })
      return null
    }
    return result.data
  } catch {
    return null
  }
}

export async function setWorkspaceMeta(wid: string, meta: WorkspaceMeta): Promise<void> {
  try {
    await set(metaKey(wid), meta, store)
  } catch {
    /* ignore */
  }
}

/**
 * Atomically write both a workspace document and meta in a single IDB transaction.
 * If either write fails, both are rolled back.
 */
export async function setDocAndMeta(
  wid: string,
  layoutId: string,
  doc: WorkspaceDocument,
  meta: WorkspaceMeta,
): Promise<void> {
  try {
    await txWrite((idbStore) => {
      idbStore.put(doc, docKey(wid, layoutId))
      idbStore.put(meta, metaKey(wid))
    })
  } catch {
    /* ignore — IDB write failure is non-critical */
  }
}

// Chart settings profile records are NOT cached in IDB.
//
// The profile list is only visible when the user opens the Settings
// modal; each chart's *current* settings live in `panel.settings`,
// which is already part of the cached layout document. So there's no
// cold-paint flicker to optimize — the modal can fetch on demand.

/**
 * Full wipe of ONE workspace scope: every `doc:{wid}:*` entry plus that
 * scope's `meta`. Deliberately scoped to those two families.
 *
 * What survives, on purpose:
 *   - `docbak:{wid}:*` — the last-known-good backups. They exist to be the
 *     recovery artifact when something destructive goes wrong, so a
 *     destructive path must not take them with it.
 *   - `profile:*` / `setting:*` / `profileCollection:*` — unrelated caches
 *     that merely share a name segment with the scope.
 *
 * The previous filter matched `k.includes(`:${wid}:`)`. Keys are `:`-joined,
 * so that substring also caught `{sub}:docbak:{wid}:{layoutId}` and, for a
 * scope whose name collides with a profile page, `{sub}:profile:{page}:*`
 * and `{sub}:setting:{page}:*`. `profilePage="history"` is a real value, so
 * `clearWorkspaceCache('history')` reached into caches it did not own.
 *
 * If you are adding a sign-out clear (a known gap), this is the right
 * primitive — call it per scope. Do NOT widen the filter back to a substring
 * match to make it cover more; add the extra key families explicitly.
 */
export async function clearWorkspaceCache(wid: string): Promise<void> {
  try {
    const docPrefix = buildIdbKey(getStorageUserScope(), 'doc', wid) + ':'
    const meta = metaKey(wid)
    const allKeys = await keys(store)
    const toDelete = (allKeys as string[]).filter((k) => k.startsWith(docPrefix) || k === meta)
    await Promise.all(toDelete.map((k) => del(k, store)))
  } catch {
    /* ignore */
  }
}

/**
 * Surgically delete only `doc:` entries whose layoutId is NOT in keepLayoutIds.
 * Meta is untouched. Use this for revision-bump reconciliation instead of
 * `clearWorkspaceCache` — wiping all docs and re-seeding from the server can
 * destroy user state if the server returns a stale/empty active layout.
 */
export async function pruneStaleDocs(wid: string, keepLayoutIds: string[]): Promise<void> {
  try {
    const docPrefix = `${getStorageUserScope()}:doc:${wid}:`
    const keep = new Set(keepLayoutIds)
    const allKeys = await keys(store)
    const toDelete = (allKeys as string[]).filter((k) => {
      if (!k.startsWith(docPrefix)) return false
      const layoutId = k.slice(docPrefix.length)
      // Only tree layouts (`lay_` ids, see tree-state-manager) are the tree's
      // to prune. Presets, the Tags page, Dashboard Home and Research keep docs
      // under their own ids, and for the browser-only ones this is the only copy.
      if (!layoutId.startsWith('lay_')) return false
      return !keep.has(layoutId)
    })
    await Promise.all(toDelete.map((k) => del(k, store)))
  } catch {
    /* ignore */
  }
}

// ─── Profile cache (user-scoped) ───
//
// Profile records (filter / column / aggregate / color presets, plus the
// expression library) all live under a single shared shape — an array of
// profile entries — keyed by (page, type). Cold-start cache for them lives
// in this IDB store under `{userSub}:profile:{page}:{type}`.
//
// Two-level semantics:
//   - RTK Query is the runtime source of truth (in memory).
//   - IDB is the cold-start cache. On first read, IDB returns either the
//     last-known payload or null; the caller's RTK fetch then overlays
//     the authoritative server data and writes back to IDB.

function profileKey(page: string, type: string): string {
  return buildIdbKey(getStorageUserScope(), 'profile', page, type)
}

/**
 * Read the cached profile array for `(page, type)`. Returns `null` when
 * no cached entry exists yet (cold start) or when validation fails.
 */
export async function getProfileCache<T = unknown>(
  page: string,
  type: string,
): Promise<T[] | null> {
  try {
    const raw = await get(profileKey(page, type), store)
    if (!Array.isArray(raw)) return null
    return raw as T[]
  } catch {
    return null
  }
}

/** Write the profile array for `(page, type)`. Best-effort; errors swallowed. */
export async function setProfileCache<T = unknown>(
  page: string,
  type: string,
  profiles: T[],
): Promise<void> {
  try {
    await set(profileKey(page, type), profiles, store)
  } catch {
    /* ignore — IDB write failure is non-critical */
  }
}

// ─── User Settings cache (singletons + expressions) ───
//
// Separate IDB namespace from `profile` so settings rows (favorites,
// expression libraries) can't collide with same-(page,type) profile
// caches. Same shape: an array of opaque entries.

function settingKey(page: string, type: string): string {
  return buildIdbKey(getStorageUserScope(), 'setting', page, type)
}

export async function getSettingsCache<T = unknown>(
  page: string,
  type: string,
): Promise<T[] | null> {
  try {
    const raw = await get(settingKey(page, type), store)
    if (!Array.isArray(raw)) return null
    return raw as T[]
  } catch {
    return null
  }
}

export async function setSettingsCache<T = unknown>(
  page: string,
  type: string,
  data: T[],
): Promise<void> {
  try {
    await set(settingKey(page, type), data, store)
  } catch {
    /* ignore — IDB write failure is non-critical */
  }
}

// ─── Profile Collection cache (sibling of getProfileCache) ───
//
// Stores the full `{profiles, tree}` collection returned by the collection
// endpoint. Same two-level semantics as the flat profile cache: RTK Query is
// the in-memory source of truth, IDB is the cold-start cache. No explicit
// invalidation — every successful fetch overwrites.

function profileCollectionKey(page: string, type: string): string {
  return buildIdbKey(getStorageUserScope(), 'profileCollection', page, type)
}

/** Read the cached collection for `(page, type)`. Null if no entry or read error. */
export async function getProfileCollectionCache<T = unknown>(
  page: string,
  type: string,
): Promise<T | null> {
  try {
    const raw = await get(profileCollectionKey(page, type), store)
    if (raw == null || typeof raw !== 'object') return null
    return raw as T
  } catch {
    return null
  }
}

/** Evict the cached collection for `(page, type)`. Best-effort. Used when a
 *  cached entry fails schema validation. An old-shape record would otherwise
 *  paint on every cold start until the user happened to save. */
export async function deleteProfileCollectionCache(page: string, type: string): Promise<void> {
  try {
    await del(profileCollectionKey(page, type), store)
  } catch {
    /* ignore: IDB delete failure is non-critical */
  }
}

/** Write the collection for `(page, type)`. Best-effort; errors swallowed. */
export async function setProfileCollectionCache<T = unknown>(
  page: string,
  type: string,
  collection: T,
): Promise<void> {
  try {
    await set(profileCollectionKey(page, type), collection, store)
  } catch {
    /* ignore — IDB write failure is non-critical */
  }
}

// ─── One-time localStorage → IndexedDB migration ───

interface MigrationResult {
  doc: WorkspaceDocument | null
  meta: WorkspaceMeta | null
}

export async function migrateFromLocalStorage(
  wid: string,
  layoutId: string,
): Promise<MigrationResult> {
  try {
    // Read scattered localStorage keys
    const layoutRaw = localStorage.getItem(`edge-layout-${wid}-${layoutId}`)
    const panelsRaw = localStorage.getItem(`edge-panels-${wid}-${layoutId}`)
    const cacheVersionRaw = localStorage.getItem(`edge-cache-version-${wid}`)
    const activeLayoutRaw = localStorage.getItem(`edge-active-layout-${wid}`)

    // Nothing to migrate
    if (!layoutRaw && !panelsRaw) return { doc: null, meta: null }

    // Assemble WorkspaceDocument
    let mosaicTree: unknown = null
    let windowConfigs: Record<string, unknown> = {}
    let globalSettings: Record<string, unknown> = {}

    if (layoutRaw) {
      const parsed = JSON.parse(layoutRaw)
      // Handle versioned format from use-layout-persistence
      const data = parsed?.version != null ? parsed.data : parsed
      if (data) {
        mosaicTree = data.mosaicTree ?? null
        windowConfigs = data.windowConfigs ?? {}
        globalSettings = data.globalSettings ?? {}
      }
    }

    // Build panels map from array
    const panels: Record<string, Record<string, unknown>> = {}
    if (panelsRaw) {
      const arr = JSON.parse(panelsRaw)
      if (Array.isArray(arr)) {
        for (const p of arr) {
          const id = p?.panelId ?? p?.chartId
          if (id) panels[id as string] = p
        }
      }
    }

    const doc: WorkspaceDocument = {
      version: 2,
      mosaicTree: mosaicTree as WorkspaceDocument['mosaicTree'],
      windowConfigs: windowConfigs as WorkspaceDocument['windowConfigs'],
      globalSettings: globalSettings as WorkspaceDocument['globalSettings'],
      panels,
    }

    // Assemble WorkspaceMeta. The legacy layouts cache key is ignored — the
    // next successful bootstrap will populate treeItems from the server. We
    // still consume `cacheVersionRaw` and `activeLayoutRaw` so the user lands
    // on their last-known active layout if the server is briefly unreachable.
    const meta: WorkspaceMeta = {
      version: 2,
      workspaceRevision: cacheVersionRaw ? Number(cacheVersionRaw) : 0,
      activeLayoutId: activeLayoutRaw,
    }

    // Write to IDB
    await setWorkspaceDoc(wid, layoutId, doc)
    await setWorkspaceMeta(wid, meta)

    // Clear migrated localStorage keys
    const keysToRemove = [
      `edge-layout-${wid}-${layoutId}`,
      `edge-panels-${wid}-${layoutId}`,
      `edge-templates-${wid}`,
      `edge-cache-version-${wid}`,
      `edge-active-layout-${wid}`,
      `edge-layouts-cache-${wid}`,
    ]
    for (const key of keysToRemove) localStorage.removeItem(key)

    return { doc, meta }
  } catch {
    return { doc: null, meta: null }
  }
}
